import { useState, useRef, useEffect } from 'react';
import { usePDIContext } from '../context/PDIContext';
import DealerHelper from './DealerHelper';
import PhotoLightbox from './PhotoLightbox';
import PhotoAnnotator from './PhotoAnnotator';
import { DELIVERY_BLOCKERS } from './DeliveryBlockerBanner';
import {
  X,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Circle,
  Lightbulb,
  AlertOctagon,
  Camera,
  Trash2,
  Wrench,
  MessageSquare,
  ShieldX,
  Info,
  Sparkles,
  ZoomIn,
  Pencil,
} from 'lucide-react';

const ItemDetailModal = ({ item, onClose }) => {
  const { 
    itemStatuses, 
    updateItemStatus, 
    notes, 
    updateNote,
    photos,
    addPhoto,
    deletePhoto,
    updatePhoto,
    resolvedItems,
    markAsResolved,
  } = usePDIContext();

  const [localNote, setLocalNote] = useState(notes[item.id] || '');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [showDealerHelper, setShowDealerHelper] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [annotatingPhoto, setAnnotatingPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  
  const status = itemStatuses[item.id] || 'pending';
  const itemPhotos = photos[item.id] || [];
  const isResolved = resolvedItems[item.id];
  const isDeliveryBlocker = DELIVERY_BLOCKERS.includes(item.id);

  // Save note when it changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localNote !== (notes[item.id] || '')) {
        updateNote(item.id, localNote);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [localNote, item.id, notes, updateNote]);

  // Handle click outside (but not when DealerHelper or PhotoLightbox is open)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if a nested overlay is open
      if (showDealerHelper || selectedPhotoIndex !== null || annotatingPhoto) {
        return;
      }
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, showDealerHelper, selectedPhotoIndex, annotatingPhoto]);

  // Handle escape key (close nested modals first)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // Close nested modals first
        if (annotatingPhoto) {
          setAnnotatingPhoto(null);
        } else if (selectedPhotoIndex !== null) {
          setSelectedPhotoIndex(null);
        } else if (showDealerHelper) {
          setShowDealerHelper(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, showDealerHelper, selectedPhotoIndex, annotatingPhoto]);

  const statusOptions = [
    { value: 'pending', label: 'Not Checked', icon: Circle, color: 'slate', gradient: 'from-slate-400 to-slate-500' },
    { value: 'pass', label: 'Pass', icon: CheckCircle2, color: 'brand', gradient: 'from-brand-400 to-emerald-500' },
    { value: 'fail', label: 'Fail', icon: XCircle, color: 'danger', gradient: 'from-danger-400 to-rose-500' },
    { value: 'attention', label: 'Attention', icon: AlertTriangle, color: 'accent', gradient: 'from-accent-400 to-orange-500' },
  ];

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploadingPhoto(true);
    
    for (const file of files) {
      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          await addPhoto(item.id, event.target.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    }
    
    setIsUploadingPhoto(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    await deletePhoto(photoId, item.id);
  };

  const getStatusButtonClass = (opt) => {
    const isSelected = status === opt.value;
    const baseClasses = 'flex-1 py-3.5 px-2 rounded-xl text-sm font-bold transition-all duration-300 btn-touch flex flex-col items-center gap-1.5';
    
    if (isSelected) {
      return `${baseClasses} bg-gradient-to-br ${opt.gradient} text-white shadow-lg`;
    }
    
    if (opt.color === 'brand') return `${baseClasses} bg-brand-50 text-brand-600 border-2 border-brand-200 hover:border-brand-300`;
    if (opt.color === 'danger') return `${baseClasses} bg-danger-50 text-danger-600 border-2 border-danger-200 hover:border-danger-300`;
    if (opt.color === 'accent') return `${baseClasses} bg-accent-50 text-accent-600 border-2 border-accent-200 hover:border-accent-300`;
    return `${baseClasses} bg-slate-50 text-slate-600 border-2 border-slate-200 hover:border-slate-300`;
  };

  return (
    <div className="modal-backdrop flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div 
        ref={modalRef}
        className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up shadow-2xl"
      >
        {/* Header */}
        <div className="relative px-5 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-bold text-slate-800 text-xl flex items-center gap-2 flex-wrap">
                {item.title}
                {item.critical && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-danger-500 to-rose-500 text-white text-[10px] rounded-lg font-bold shadow-sm">
                    <AlertOctagon className="w-3 h-3" />
                    CRITICAL
                  </span>
                )}
              </h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{item.description}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Tip */}
          {item.tip && (
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-4">
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl" />
              <div className="relative flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-blue-800 text-sm">Pro Tip</p>
                  <p className="text-sm text-blue-700 mt-1 leading-relaxed">{item.tip}</p>
                </div>
              </div>
            </div>
          )}

          {/* Warning */}
          {item.warning && (
            <div className="relative overflow-hidden bg-gradient-to-br from-danger-50 to-rose-50 border-2 border-danger-200 rounded-2xl p-4">
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-danger-200/30 rounded-full blur-2xl" />
              <div className="relative flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-danger-500 to-rose-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                  <AlertOctagon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-danger-800 text-sm">Warning</p>
                  <p className="text-sm text-danger-700 mt-1 leading-relaxed">{item.warning}</p>
                </div>
              </div>
            </div>
          )}

          {/* Why It Matters */}
          {item.whyItMatters && (
            <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 rounded-2xl p-4">
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-purple-200/30 rounded-full blur-2xl" />
              <div className="relative flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Info className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-purple-800 text-sm">Why This Matters</p>
                  <p className="text-sm text-purple-700 mt-1 leading-relaxed">{item.whyItMatters}</p>
                </div>
              </div>
            </div>
          )}

          {/* Delivery Blocker Warning */}
          {isDeliveryBlocker && (
            <div className="relative overflow-hidden bg-slate-900 rounded-2xl p-4">
              <div className="absolute inset-0 bg-gradient-to-br from-danger-500/20 via-transparent to-purple-500/20" />
              <div className="relative flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-danger-500 to-rose-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                  <ShieldX className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-white text-sm">🚫 Delivery Blocker</p>
                  <p className="text-sm text-slate-300 mt-1 leading-relaxed">
                    If this item fails, do NOT accept delivery until resolved.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status Selection */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-500" />
              Set Status
            </label>
            <div className="grid grid-cols-4 gap-2">
              {statusOptions.map(opt => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => updateItemStatus(item.id, opt.value)}
                    className={getStatusButtonClass(opt)}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[10px] leading-tight">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Resolved by Dealer (only show for fail/attention) */}
          {(status === 'fail' || status === 'attention') && (
            <>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl border-2 border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-slate-500" />
                  </div>
                  <span className="text-sm font-semibold text-slate-700">Resolved by dealer</span>
                </div>
                <button
                  onClick={() => markAsResolved(item.id, !isResolved)}
                  className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                    isResolved ? 'bg-gradient-to-r from-brand-500 to-emerald-500 shadow-lg shadow-brand-500/30' : 'bg-slate-300'
                  }`}
                >
                  <span 
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${
                      isResolved ? 'left-8' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              {/* Dealer Conversation Helper Button */}
              <button
                onClick={() => setShowDealerHelper(true)}
                className="w-full p-4 bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-2xl flex items-center justify-between btn-touch shadow-xl hover:shadow-2xl transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-teal-400 flex items-center justify-center shadow-lg">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-sm">Dealer Conversation Helper</p>
                    <p className="text-xs text-slate-400">What to say & what NOT to accept</p>
                  </div>
                </div>
                <span className="text-brand-400 text-xl group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-purple-500" />
              Notes
            </label>
            <textarea
              value={localNote}
              onChange={(e) => setLocalNote(e.target.value)}
              placeholder="Add notes about this item..."
              rows={3}
              className="resize-none rounded-2xl"
            />
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <Camera className="w-4 h-4 text-pink-500" />
              Photos
            </label>
            
            {/* Photo Grid */}
            {itemPhotos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {itemPhotos.map((photo, index) => (
                  <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden group shadow-md">
                    <img 
                      src={photo.data} 
                      alt="Inspection photo"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                      onClick={() => setSelectedPhotoIndex(index)}
                    />
                    {/* Zoom indicator */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                      <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnnotatingPhoto(photo);
                      }}
                      className="absolute top-1.5 left-1.5 w-7 h-7 rounded-lg bg-slate-900/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg hover:scale-110"
                      aria-label="Mark up photo"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photo.id);
                      }}
                      className="absolute top-1.5 right-1.5 w-7 h-7 rounded-lg bg-danger-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg hover:scale-110"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingPhoto}
              className="dropzone w-full rounded-2xl border-2"
            >
              {isUploadingPhoto ? (
                <>
                  <div className="w-10 h-10 border-3 border-brand-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-slate-500 font-medium">Uploading...</span>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <Camera className="w-6 h-6 text-slate-400" />
                  </div>
                  <span className="text-sm text-slate-600 font-semibold">Tap to add photos</span>
                  <span className="text-xs text-slate-400">Stored locally on your device</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 bg-gradient-to-r from-white to-slate-50 safe-bottom">
          <button
            onClick={onClose}
            className="w-full gradient-animated text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-500/25 btn-touch text-lg"
          >
            Done
          </button>
        </div>
      </div>

      {/* Dealer Helper Modal */}
      {showDealerHelper && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={(e) => {
            e.stopPropagation();
            // Only close if clicking on backdrop, not on the content
            if (e.target === e.currentTarget) {
              setShowDealerHelper(false);
            }
          }}
        >
          <div onClick={(e) => e.stopPropagation()} className="max-w-md w-full max-h-[80vh] overflow-y-auto">
            <DealerHelper item={item} onClose={() => setShowDealerHelper(false)} />
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      {selectedPhotoIndex !== null && itemPhotos.length > 0 && (
        <PhotoLightbox
          photos={itemPhotos}
          selectedIndex={selectedPhotoIndex}
          onClose={() => setSelectedPhotoIndex(null)}
          onNavigate={(index) => setSelectedPhotoIndex(index)}
        />
      )}

      {/* Photo Annotator */}
      {annotatingPhoto && (
        <PhotoAnnotator
          src={annotatingPhoto.data}
          onCancel={() => setAnnotatingPhoto(null)}
          onSave={async (dataUrl) => {
            await updatePhoto(annotatingPhoto.id, item.id, dataUrl);
            setAnnotatingPhoto(null);
          }}
        />
      )}
    </div>
  );
};

export default ItemDetailModal;
